import React, { useEffect, useState } from 'react';
import WeeklyDrawPrizeTable from 'components/WeeklyDrawPrizeTable';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import { ASSET_PREFIX } from 'utils/constants';
import Subheading, { SubheadingColourScheme } from 'components/Subheading';
import { EntryMethod, EntryMethodDetails } from 'components/enter/EntryMethod';
import RoundedButton from 'components/RoundedButton';
import Link from 'next/link';
import CardWithBackgroundImage from 'components/CardWithBackgroundImage';
import GreenPageSection from 'components/GreenPageSection';
import { observer } from 'mobx-react-lite';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { ENTER_CTA_LINK } from 'utils/cta/CTA';

interface IPrizesTableAndPlantTreeSection {
  entryMethod: keyof typeof EntryMethod;
}

const PrizesTableAndPlantTreeSection = observer(
  ({ entryMethod }: IPrizesTableAndPlantTreeSection) => {
    const [expanded, setExpanded] = useState(false);
    const { prizesWonCount } = useDrawStore();
    let prizeTableType, sectionBackground;
    switch (entryMethod) {
      case EntryMethod.FreeSubscription:
        prizeTableType = 'FREE';
        sectionBackground =
          EntryMethodDetails.FreeSubscription.sectionBackground;
        break;

      case EntryMethod.LiteSubscription:
        prizeTableType = 'LITE';
        sectionBackground =
          EntryMethodDetails.LiteSubscription.sectionBackground;
        break;

      case EntryMethod.PremiumSubscription:
      case EntryMethod.OneOff:
      default:
        prizeTableType = 'PREMIUM';
        sectionBackground =
          EntryMethodDetails.PremiumSubscription.sectionBackground;
        break;
    }

    const oneOff = entryMethod === EntryMethod.OneOff;

    const IMAGE_DIR = '/enter/prizes-table-and-plant-tree-section';

    return (
      <div className="prizesTableAndPlantTreeSection fullWidth flexCentered">
        <GreenPageSection>
          <Subheading textShadowColor="white">
            What Prizes Can You <strong>Win?</strong>
          </Subheading>

          <div className="prizeTableContainer">
            <WeeklyDrawPrizeTable
              type={prizeTableType}
              expanded={expanded}
              withPrizesPageLinks={false}
            />

            {!expanded && entryMethod !== EntryMethod.FreeSubscription && (
              <div
                style={{
                  textAlign: 'center',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  margin: '2em 2em 0',
                }}
                onClick={() => setExpanded(true)}
              >
                <img
                  src={`${ASSET_PREFIX}/images/plus-circle.svg`}
                  onClick={() => setExpanded(true)}
                  style={{ width: '28px', height: '28px' }}
                />
                <p
                  style={{
                    margin: '0.5em 0 0',
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    fontSize: '1.1em',
                  }}
                >
                  See More
                </p>
              </div>
            )}

            {entryMethod !== EntryMethod.FreeSubscription && (
              <p
                style={{
                  maxWidth: 800,
                  margin: '3em 0 2em',
                  textAlign: 'center',
                }}
              >
                The more numbers you match, the bigger the prize value! Your
                chance to win a trip of a lifetime or epic prize could be{' '}
                <em>just a few numbers away</em>.
              </p>
            )}
          </div>

          <div className="cardsContainer fullWidth flexCentered">
            <CardWithBackgroundImage
              imageDirectory={IMAGE_DIR}
              imageFname="card-bg-plant-a-tree"
            >
              {entryMethod === EntryMethod.FreeSubscription ? (
                <div className="cardText flexCentered">
                  <Subheading
                    className="overrideColourScheme"
                    colourScheme={
                      SubheadingColourScheme.WHITE_TEXT_BLACK_SHADOW
                    }
                  >
                    Want to plant some trees?
                  </Subheading>

                  <p>Try our Premium plan to plant a tree every single week!</p>

                  <RoundedButton href={ENTER_CTA_LINK}>
                    Try Premium
                  </RoundedButton>
                </div>
              ) : (
                <div className="cardText flexCentered">
                  <Subheading
                    className="overrideColourScheme"
                    colourScheme={
                      SubheadingColourScheme.WHITE_TEXT_BLACK_SHADOW
                    }
                  >
                    Plant A Tree {oneOff ? '' : 'Every Week'}
                  </Subheading>

                  <p>
                    You'll plant a tree with {''}
                    <Link href="https://www.veritree.com">
                      <a
                        style={{ textDecoration: 'underline' }}
                        target="_blank"
                      >
                        veritree
                      </a>
                    </Link>
                    {oneOff ? '.' : " every week you're subscribed."}
                  </p>
                </div>
              )}
            </CardWithBackgroundImage>

            {prizesWonCount && (
              <CardWithBackgroundImage
                id="winnersCard"
                imageDirectory={IMAGE_DIR}
                imageFname="card-bg-winners"
              >
                <div className="cardText flexCentered">
                  <Subheading
                    className="overrideColourScheme"
                    colourScheme={
                      SubheadingColourScheme.WHITE_TEXT_BLACK_SHADOW
                    }
                    style={{ maxWidth: '240px' }}
                  >
                    Thousands of winners every week
                  </Subheading>

                  <p>Will YOU be next?</p>
                </div>
              </CardWithBackgroundImage>
            )}
          </div>

          <style jsx>
            {`
              .prizeTableContainer {
                padding: 1.5em 1.8em;
                border-radius: 8px;
                background-color: white;
                margin: 2em auto;
                width: 100%;
                max-width: 980px;
                display: flex;
                flex-direction: column;
                align-items: center;
              }

              .moreLessButton {
                display: flex;
                flex-direction: column;
                justify-content: flex-center;
                background-color: clear;
                font-weight: bold;
                cursor: pointer;
                align-items: center;
                text-align: center;
                font-style: italic;
                font-size: 1.1em;
              }

              :global(
                  .prizesTableAndPlantTreeSection .cardWithBackgroundImage
                ) {
                border-radius: 10px;
                padding: 2em;
                text-align: center;
                color: white;
                margin: 0.5em 0;
              }

              :global(
                  .prizesTableAndPlantTreeSection
                    .cardWithBackgroundImage
                    .subheading
                ) {
                margin-top: 0;
                margin-bottom: 0.3em;
              }

              :global(
                  .prizesTableAndPlantTreeSection .cardWithBackgroundImage a
                ) {
                color: white;
                white-space: normal;
              }

              :global(
                  .prizesTableAndPlantTreeSection
                    .cardWithBackgroundImage
                    .roundedButton
                ) {
                border: 2px solid white;
                color: white;
                background: transparent;
                min-width: 200px;
              }

              .cardText {
                width: 100%;
                max-width: 300px;
              }

              :global(
                  .prizesTableAndPlantTreeSection #trustpilot-rating-widget
                ) {
                margin: 3em 0;
              }

              @media (min-width: 800px) {
                .prizeTableContainer {
                  padding: 2em 3em;
                }

                .cardsContainer {
                  flex-direction: row;
                }

                :global(.cardsContainer .cardWithBackgroundImage) {
                  align-self: stretch;
                }

                :global(#winnersCard) {
                  margin-left: 2%;
                }
              }
            `}
          </style>
        </GreenPageSection>
      </div>
    );
  }
);

export default PrizesTableAndPlantTreeSection;
